/**
 * EcosystemCardsView: the card grid of hosted ecosystems.
 *
 * Presentational view rendered by EcosystemsPage (the `?view=cards` tab).
 * Receives the already-fetched ecosystem list as a prop: no fetching, no
 * page shell here. Extracted from the former standalone EcosystemDirectory
 * page when the card and graph views were merged behind one route (#241).
 *
 * Each card shows the ecosystem's logo, name, a tagline (its `shortName`, or
 * a short slice of its `description`), its active-member count, and a "Visit"
 * action that navigates into that ecosystem in path mode (`/{slug}`).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card, Button, Badge } from 'react-bootstrap';

import { getEcosystemDestinationUrl } from '@/shared/utils/ecosystemRouting';

import { deriveTagline } from './ecosystemDisplay';
import './EcosystemsPage.css';

export default function EcosystemCardsView({ ecosystems }) {
    return (
        <Row className="g-4">
            {ecosystems.map((eco) => (
                <Col md={6} lg={4} key={eco.slug}>
                    <Card className="h-100 border-0 border-top border-4 border-primary shadow-sm ecosystem-directory-card">
                        <Card.Body className="p-4 d-flex flex-column">
                            <div className="ecosystem-directory-logo-frame d-flex align-items-center justify-content-center mb-3">
                                {eco.branding?.logoUrl ? (
                                    <img
                                        src={eco.branding.logoUrl}
                                        alt={`${eco.name} logo`}
                                        className="ecosystem-directory-logo"
                                    />
                                ) : (
                                    <span className="display-5 text-muted">
                                        🌐
                                    </span>
                                )}
                            </div>
                            <h5 className="text-primary mb-2">{eco.name}</h5>
                            <p className="text-muted small flex-grow-1">
                                {deriveTagline(eco)}
                            </p>
                            <div className="mb-3">
                                <Badge bg="secondary" pill>
                                    {eco.memberCount || 0}{' '}
                                    {eco.memberCount === 1
                                        ? 'member'
                                        : 'members'}
                                </Badge>
                            </div>
                            <div className="d-grid mt-auto">
                                {/*
                                 * Round-4 review finding 7: render
                                 * as a plain anchor (full
                                 * navigation), NOT a React Router
                                 * <Link>. The platform apex mounts
                                 * BrowserRouter with basename='/';
                                 * a client-side <Link to={`/${slug}`}>
                                 * push would not re-bootstrap
                                 * EcosystemProvider (its boot
                                 * useEffect has empty deps),
                                 * leaving basePath='' so the new
                                 * tenant URL doesn't match any
                                 * route and the user lands on the
                                 * catch-all 404. A full reload
                                 * lets the tenant resolver re-run
                                 * and BrowserRouter remount with
                                 * basename='/<slug>'. Cross-tenant
                                 * navigation is a context switch,
                                 * so the reload cost is acceptable.
                                 *
                                 * v1.0.1 #56: route through
                                 * getEcosystemDestinationUrl so a
                                 * user on a tenant subdomain
                                 * (e.g. costem.rileconnect.org)
                                 * gets an absolute URL pointing
                                 * at the target tenant's
                                 * canonical host instead of
                                 * `costem.rileconnect.org/<slug>`,
                                 * which would shadow the wrong
                                 * tenant.
                                 */}
                                <Button
                                    as="a"
                                    href={getEcosystemDestinationUrl(eco)}
                                    variant="primary"
                                    className="rounded-pill"
                                >
                                    Visit {eco.shortName || eco.name}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            ))}
        </Row>
    );
}

EcosystemCardsView.propTypes = {
    ecosystems: PropTypes.arrayOf(
        PropTypes.shape({
            slug: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            shortName: PropTypes.string,
            memberCount: PropTypes.number,
            branding: PropTypes.shape({
                logoUrl: PropTypes.string,
            }),
        })
    ).isRequired,
};
